import { WorkflowGraph } from 'cio/utils/visual-workflow-builder/workflow-graph';
import { LAYER_HEIGHT, NODE_SEP } from 'cio/utils/visual-workflow-builder/layout/size';
import lodashSum from 'lodash.sum';
import { set } from '@ember/object';

const HALF_DISTANCE_BETWEEN_NODES = Math.floor(NODE_SEP / 2);

// Source material:
// http://citeseerx.ist.psu.edu/viewdoc/download?doi=10.1.1.64.4457&rep=rep1&type=pdf
// https://arxiv.org/pdf/2008.01252.pdf
// https://github.com/cpettitt/dig.js
// https://github.com/dagrejs/dagre

function makeOrderArray(graph: WorkflowGraph) {
  let ordering: string[][] = [];

  Object.values(graph.nodes).forEach((n) => {
    let nodeRank = graph.ranks[n.id];
    let rank = ordering[nodeRank];

    if (rank == null) {
      ordering[nodeRank] = [];
      rank = ordering[nodeRank];
    }

    let rankOrder = graph.layeredRanks[nodeRank]?.indexOf(n.id) ?? 0;
    rank[rankOrder] = n.id;
  });

  return ordering;
}

function makeOrderMap(ordering: string[][]) {
  let map: Record<string, { rank: number; order: number }> = {};

  for (let i = 0; i < ordering.length; ++i) {
    for (let j = 0; j < ordering[i].length; ++j) {
      map[ordering[i][j]] = {
        rank: i,
        order: j,
      };
    }
  }

  return map;
}

function makeMedianMap(
  graph: WorkflowGraph,
  orderArray: string[][],
  orderMap: Record<string, { rank: number; order: number }>,
) {
  return findMedians(graph, orderArray, orderMap);
}

function findMedians(
  graph: WorkflowGraph,
  orderArray: string[][],
  orderMap: Record<string, { rank: number; order: number }>,
) {
  let medians: Record<string, string[]> = {};

  Object.values(graph.nodes).forEach((n) => {
    medians[n.id] = [];
  });

  let prevLayer = null;
  for (let i = 0; i < orderArray.length; ++i) {
    let currLayer = orderArray[i];
    if (prevLayer !== null) {
      for (let j = 0; j < currLayer.length; ++j) {
        let u = currLayer[j];
        let preds: string[] = [];
        let neighbors = findNeighbors(u, graph);
        neighbors.forEach((v) => {
          if (orderMap[v].rank === i - 1) {
            preds.push(v);
          }
        });
        let vs = preds.sort((a, b) => orderMap[a].order - orderMap[b].order);
        if (vs.length > 0) {
          // The Brandes-Kopf algorithm aligns items with the median upper
          // neighbor, but we align it with the first neighbor instead to
          // produce a layout that satisfies our visual preferences better (for
          // example, we want rejoin nodes to stay as close as possible to the
          // center of all parent branches)
          medians[u] = [vs[0]];
        }
      }
    }
    prevLayer = currLayer;
  }

  return medians;
}

function verticalAlignment(
  graph: WorkflowGraph,
  orderArray: string[][],
  orderMap: Record<string, { rank: number; order: number }>,
  medianMap: Record<string, string[]>,
) {
  let root: Record<string, string> = {};
  let align: Record<string, string> = {};
  let pos: Record<string, number> = {};
  Object.values(graph.nodes).forEach((n) => {
    root[n.id] = n.id;
    align[n.id] = n.id;
    pos[n.id] = orderMap[n.id]?.order ?? 0;
  });

  for (let i = 0; i < orderArray.length; ++i) {
    let r = -1;
    let currentLayer = orderArray[i];
    for (let j = 0; j < currentLayer.length; ++j) {
      let v = currentLayer[j];
      for (let k = 0; k < medianMap[v].length; ++k) {
        if (align[v] === v) {
          let u = medianMap[v][k];
          let uPos = orderMap[u].order;
          if (r < uPos) {
            align[u] = v;
            root[v] = root[u];
            align[v] = root[v];
            r = uPos;
          }
        }
      }
    }
  }

  return {
    root,
    align,
  };
}
function horizontalCompaction(
  graph: WorkflowGraph,
  orderArray: string[][],
  orderMap: Record<string, { rank: number; order: number }>,
  alignment: {
    root: Record<string, string>;
    align: Record<string, string>;
  },
) {
  let { root, align } = alignment;

  let sink: Record<string, string> = {};
  let shift: Record<string, number> = {};
  let x: Record<string, number> = {};
  Object.values(graph.nodes).forEach((n) => {
    sink[n.id] = n.id;
    shift[n.id] = Number.POSITIVE_INFINITY;
  });

  function deltaUV(u: string, v: string) {
    let uN = graph.nodes[u];
    let vN = graph.nodes[v];
    return Math.floor(uN.width / 2 + vN.width / 2 + NODE_SEP);
  }

  function placeBlock(v: string) {
    if (x[v] == null) {
      x[v] = 0;
      let w = v;
      do {
        if (orderMap[w]?.order ?? 0 > 0) {
          let pred = orderArray[orderMap[w].rank][orderMap[w].order - 1];
          let u = root[pred];
          placeBlock(u);
          x[v] = Math.floor(Math.max(x[v] ?? 0, (x[pred] ?? 0) + deltaUV(pred, w)));
        }
        w = align[w];
      } while (w !== v);

      while (align[w] !== v) {
        w = align[w];
        x[w] = Math.floor(x[v]);
      }
    }
  }

  Object.values(graph.nodes).forEach((n) => {
    if (root[n.id] === n.id) {
      placeBlock(n.id);
    }
  });

  return x;
}

function pass(
  graph: WorkflowGraph,
  orderArray: string[][],
  orderMap: Record<string, { rank: number; order: number }>,
  medianMap: Record<string, string[]>,
) {
  let alignment = verticalAlignment(graph, orderArray, orderMap, medianMap);
  return horizontalCompaction(graph, orderArray, orderMap, alignment);
}

function flipOrderHoriz(ordering: string[][]) {
  for (let i = 0; i < ordering.length; ++i) {
    ordering[i].reverse();
  }
}

// function flipOrderVert(ordering: string[][]) {
// ordering.reverse();
// }

function flipValuesHorizontal(xs: Record<string, number>) {
  return Object.entries(xs).reduce<Record<string, number>>((acc, [u, x]) => {
    acc[u] = -x;
    return acc;
  }, {});
}

function findNeighbors(
  nodeId: string,
  graph: WorkflowGraph,
  verticalTraversalDirection: 'up' | 'down' | 'both' = 'both',
) {
  let neighbors = [];
  if (verticalTraversalDirection === 'up' || verticalTraversalDirection === 'both') {
    // If we're traversing up-wards, we're looking for node successors
    neighbors.push(...(graph.outEdges[nodeId] ?? []).map((e) => e.to));
  }
  if (verticalTraversalDirection === 'down' || verticalTraversalDirection === 'both') {
    // If we're traversing down-wards, we're looking for node predecessors
    neighbors.push(...(graph.inEdges[nodeId] ?? []).map((e) => e.from));
  }
  return neighbors.uniq();
}

function upperLeft(graph: WorkflowGraph) {
  let orderArray = makeOrderArray(graph);
  let orderMap = makeOrderMap(orderArray);
  let medianMap = makeMedianMap(graph, orderArray, orderMap);
  return pass(graph, orderArray, orderMap, medianMap);
}

function upperRight(graph: WorkflowGraph) {
  let orderArray = makeOrderArray(graph);
  flipOrderHoriz(orderArray);
  let orderMap = makeOrderMap(orderArray);
  let medianMap = makeMedianMap(graph, orderArray, orderMap);
  return flipValuesHorizontal(pass(graph, orderArray, orderMap, medianMap));
}

// We currently only use the upper left and right passes, as those seem to
// produce good enough layouts
// function lowerRight(graph: WorkflowGraph) {
// let orderArray = makeOrderArray(graph);
// flipOrderHoriz(orderArray);
// flipOrderVert(orderArray);
// let orderMap = makeOrderMap(orderArray);
// let medianMap = makeMedianMap(graph, orderArray, orderMap);
// return flipValuesHorizontal(pass(graph, orderArray, orderMap, medianMap));
// }

// function lowerLeft(graph: WorkflowGraph) {
// let orderArray = makeOrderArray(graph);
// flipOrderVert(orderArray);
// let orderMap = makeOrderMap(orderArray);
// let medianMap = makeMedianMap(graph, orderArray, orderMap);
// return pass(graph, orderArray, orderMap, medianMap);
// }

// Our Brandes-Kopf implementation may leave reonnect nodes slightly
// off-center, as it's aim is to create a balanced layout.
// In order for rejoin points, their drop points, and everything that follows
// to render nicely and properly centered again, we need to readjust all
// elements.
// This step is done after all unit nodes have been removed
export function recenterNodes(graph: WorkflowGraph) {
  let layers = [...Object.entries(graph.layeredRanks)]
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map((ranks) => ranks[1].map((id) => graph.nodes[id]));

  let oldXs: Record<string, number> = {};

  layers.forEach((layer, i) => {
    if (i === 0) {
      return;
    }
    layer.forEach((n) => {
      let predecessors = findNeighbors(n.id, graph, 'down');
      let nonExitPredecessors = predecessors.filter((p) => graph.nodes[p].type !== 'exit_action');
      let predecessorXs = nonExitPredecessors.map((p) => graph.nodes[p].x);
      // Don't adjust path labels - those will be shifted, if necessary, when
      // moving the parent branch node itself
      if (n.type !== 'virtual_path_label') {
        oldXs[n.id] = n.x;
        // Need to use "set", because at this point an outside component may
        // already hold references to the graph and nodes, and the coordinates
        // aren't @tracked, which makes ember complain
        if (predecessorXs.length > 1) {
          let sortedXs = predecessorXs.sort((a, b) => a - b);
          set(n, 'x', Math.floor((sortedXs[0] + sortedXs[sortedXs.length - 1]) / 2));
        } else {
          set(n, 'x', Math.floor(predecessorXs[0]));
        }

        if (n.typeCategory === 'action' && n.action.isBranch) {
          let successors = findNeighbors(n.id, graph, 'up');

          // If the branch action only has one outgoing path, exit early, as we
          // don't need to recenter anything
          if (successors.length <= 1) {
            return;
          }

          let branchLeftEdge = n.x - n.width / 2;
          let branchRightEdge = n.x + n.width / 2;

          let edges = graph.outEdges[n.id];

          let [leftMostEdge] = edges;
          let rightMostEdge = edges[edges.length - 1];

          let leftMostNode = graph.nodes[leftMostEdge.to];
          let rightMostNode = graph.nodes[rightMostEdge.to];

          // If, after shifting, a branch node ends up outside of its leftmost
          // or rightmost paths, we shift all the paths so that the branch sits
          // between them again.
          // We shift enough for the branch to sit with its edge right at
          // whichever path is closest. This is to do as little shifting as
          // possible.
          if (leftMostNode.x > branchLeftEdge || rightMostNode.x < branchRightEdge) {
            let branchCenter = (leftMostNode.x + rightMostNode.x) / 2;
            let diff = branchCenter - n.x;

            // We add half the minimum distance between nodes for a more
            // balanced-looking result.
            if (leftMostNode.x > branchLeftEdge) {
              diff = branchLeftEdge - leftMostNode.x - HALF_DISTANCE_BETWEEN_NODES;
            } else if (rightMostNode.x < branchRightEdge) {
              diff = branchRightEdge - rightMostNode.x + HALF_DISTANCE_BETWEEN_NODES;
            }

            graph.outEdges[n.id].forEach((e) => {
              let targetNode = graph.nodes[e.to];
              oldXs[targetNode.id] = targetNode.x;
              set(targetNode, 'x', Math.floor(targetNode.x + diff));
            });
          }
        }
      }
    });
  });
}

export function layout(unitNodesGraph: WorkflowGraph) {
  // Assign y coordinates based on layering
  let layers = [...Object.entries(unitNodesGraph.layeredRanks)]
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map((ranks) => ranks[1].map((id) => unitNodesGraph.nodes[id]));

  layers.forEach((layer, i) => {
    layer.forEach((node) => {
      node.y = i * LAYER_HEIGHT;
    });
  });

  let xs = [
    upperLeft(unitNodesGraph),
    upperRight(unitNodesGraph),
    // lowerRight(unitNodesGraph),
    // lowerLeft(unitNodesGraph),
  ];

  Object.values(unitNodesGraph.nodes).forEach((n) => {
    let xArray = [];
    for (let i = 0; i < xs.length; ++i) {
      xArray.push(xs[i][n.id]);
    }
    // Brandes-Kopf suggest using an "average median" of the results, but since
    // we only use two (of the four proposed) passes, we just use a regular
    // average
    // xArray.sort((a, b) => a - b);
    // n.x = (xArray[1] + xArray[2]) / 2;
    n.x = Math.floor(lodashSum(xArray) / xArray.length);
  });
}
